import type {
  ManagementCandidate,
  ManagementSegmentCatalogEntry,
} from "../types/tradeManagementReport";

export type ManagementCatalogLookupEntry = {
  entry: ManagementSegmentCatalogEntry;
  isTesCandidate: boolean;
  candidate?: ManagementCandidate;
};

export type ManagementCatalogLookup = Map<string, ManagementCatalogLookupEntry>;

export function buildManagementCatalogLookup(
  catalog: ManagementSegmentCatalogEntry[] | undefined,
  candidates: ManagementCandidate[] | undefined
): ManagementCatalogLookup {
  const map: ManagementCatalogLookup = new Map();
  const candidateKeys = new Set(
    (candidates ?? []).map((c) => String(c.segment_key ?? "").trim()).filter(Boolean)
  );
  const candidateByKey = new Map(
    (candidates ?? [])
      .filter((c) => c.segment_key)
      .map((c) => [String(c.segment_key), c] as const)
  );

  for (const entry of catalog ?? []) {
    const key = String(entry.segment_key ?? "").trim();
    if (!key) continue;
    map.set(key, {
      entry,
      isTesCandidate: candidateKeys.has(key),
      candidate: candidateByKey.get(key),
    });
  }

  for (const c of candidates ?? []) {
    const key = String(c.segment_key ?? "").trim();
    if (!key || map.has(key)) continue;
    map.set(key, { entry: c, isTesCandidate: true, candidate: c });
  }

  return map;
}

export function extractManagementSnapshotFields(inputSnapshot: unknown): {
  catalog: ManagementSegmentCatalogEntry[];
  candidates: ManagementCandidate[];
  tradeSideFilter: "both" | "long" | "short" | undefined;
} {
  if (!inputSnapshot || typeof inputSnapshot !== "object" || Array.isArray(inputSnapshot)) {
    return { catalog: [], candidates: [], tradeSideFilter: undefined };
  }
  const snap = inputSnapshot as Record<string, unknown>;
  const meta = snap.meta as Record<string, unknown> | undefined;
  const rawFilter = meta?.trade_side_filter;
  const tradeSideFilter =
    rawFilter === "long" || rawFilter === "short" || rawFilter === "both" ? rawFilter : undefined;

  return {
    catalog: Array.isArray(snap.management_segment_catalog)
      ? (snap.management_segment_catalog as ManagementSegmentCatalogEntry[])
      : [],
    candidates: Array.isArray(snap.management_candidates)
      ? (snap.management_candidates as ManagementCandidate[])
      : [],
    tradeSideFilter,
  };
}

/** Derive segment_key from configurator dimensions (regime|direction|side). */
export function segmentKeyFromConfigurator(
  regime: string,
  candleDirection: string,
  tradeDirection: string,
  candleWindow: "long" | "short" = "long"
): string | null {
  const r = String(regime ?? "").trim().toLowerCase();
  const d = String(candleDirection ?? "").trim().toLowerCase();
  const side = String(tradeDirection ?? "").trim().toLowerCase();
  if (!r || !d || !side) return null;
  if (candleWindow === "short") {
    return `${r}|${d}|${side}`;
  }
  return `${r}|${d}|${side}`;
}

export function segmentKeyFromSummaryRowKey(rowKey: string): string | null {
  const key = String(rowKey ?? "").trim();
  if (!key) return null;
  if (key.includes("|")) return key;
  return null;
}

export function segmentScopeFromSegmentKey(
  segmentKey: string,
  useShortDirection?: boolean
): string {
  const parts = String(segmentKey).split("|");
  const side = parts[parts.length - 1]?.toLowerCase();
  if (useShortDirection) return "triple_short";
  if (side === "long") return "triple_long";
  if (side === "short") return "triple_short";
  return "market_condition";
}
